import {
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Button, TextInput } from "north.js";
import React from "react";
import styled from "styled-components";

import autosuggestSparklesIconUrl, {
  ReactComponent as AutosuggestSparklesIcon,
} from "src/static/img/hackerDirectory/autosuggest-sparkles.svg";

import { StagedFilters } from "./DirectoryFilters";

const Controls = styled.form`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 32px;

  @media (max-width: 600px) {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  gap: 8px;

  @media (max-width: 600px) {
    width: 100%;

    > * {
      width: calc(50% - 4px);
      min-width: 0;
      flex: 0 0 calc(50% - 4px);
    }

    > *:only-child {
      width: 100%;
      flex-basis: 100%;
    }
  }
`;

const SearchField = styled.div`
  position: relative;
  min-width: 0;
  flex: 1 1 0%;

  > div {
    width: 100%;
  }

  input {
    width: 100%;
    min-width: 0;
  }
`;

const SearchIcon = styled(MagnifyingGlassIcon)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const SearchInput = styled(TextInput)`
  && {
    height: 44px;
    border-color: ${({ theme }) => theme.globalConstants.color.borderGray};
    border-radius: 4px;
    background: ${({ theme }) => theme.globalConstants.color.white};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    padding-right: 48px !important;

    &:hover,
    &:focus {
      border-color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
    }

    &:disabled {
      cursor: not-allowed;
      background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
      color: ${({ theme }) => theme.globalConstants.color.textTertiary};
    }
  }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  display: inline-flex;
  width: 32px;
  height: 32px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  cursor: pointer;
  transform: translateY(-50%);

  &:hover {
    background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => theme.globalConstants.color.borderPrimary};
    outline-offset: 1px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FilterButton = styled(Button)<{ $active: boolean }>`
  && {
    display: flex;
    height: 44px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 7px 20px;
    border-color: ${({ theme }) => theme.globalConstants.color.borderGray};
    border-radius: 4px;
    background: ${({ $active, theme }) =>
      $active
        ? theme.globalConstants.color.backgroundGrayDark
        : theme.globalConstants.color.backgroundGray};
    color: ${({ theme }) => theme.globalConstants.color.textSecondary};
    font-weight: 700;
    white-space: nowrap;

    &:disabled {
      cursor: not-allowed;
      color: ${({ theme }) => theme.globalConstants.color.textTertiary};
      opacity: 0.65;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 600px) {
    && {
      width: 100%;
    }
  }
`;

const AutosuggestControl = styled.div`
  position: relative;
`;

const AutosuggestButton = styled(Button)`
  && {
    display: flex;
    height: 44px;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    border: 0;
    border-radius: 4px;
    background: linear-gradient(136deg, #d1efff 0%, #e5dbff 100%);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    color: #7569e9;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;

    &:focus {
      outline: 2px solid
        ${({ theme }) => theme.globalConstants.color.borderPrimary};
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      filter: grayscale(1);
      opacity: 0.5;
    }
    &:hover:not(:disabled) {
      background: linear-gradient(136deg, #d1efff 0%, #e5dbff 100%);
      color: #7569e9;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 600px) {
    && {
      width: 100%;
    }
  }
`;

const AutosuggestDescriptionIcon = styled.img`
  display: none;

  @media (max-width: 600px) {
    display: block;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const AutosuggestSwitch = styled.span<{ $active: boolean }>`
  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  width: 34.55px;
  height: 21px;
  flex-shrink: 0;
  border: 0;
  border-radius: 999px;
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(139deg, #b55fe8 0%, #25b0eb 100%)"
      : "rgba(156, 163, 175, 0.3)"};

  &::after {
    position: absolute;
    top: 1.35px;
    left: 1.35px;
    width: 18.29px;
    height: 18.29px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 2.032px 0.677px rgba(0, 0, 0, 0.06),
      0 2.032px 5.419px rgba(0, 0, 0, 0.15), 0 0 0 0.677px rgba(0, 0, 0, 0.04);
    content: "";
    transform: translateX(${({ $active }) => ($active ? "14px" : "0")});
    transition: transform 180ms ease;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      transition: none;
    }
  }
`;

const ControlTooltip = styled.span`
  position: absolute;
  z-index: 3;
  right: 0;
  top: calc(100% + 12px);
  width: 186px;
  padding: 8px 12px;
  visibility: hidden;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1);
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  opacity: 0;
  pointer-events: none;
  text-align: center;
  transition: opacity 120ms ease, visibility 120ms ease;

  &::before,
  &::after {
    position: absolute;
    bottom: 100%;
    right: 46px;
    width: 0;
    height: 0;
    border-style: solid;
    content: "";
  }

  &::before {
    border-width: 0 7px 7px;
    border-color: transparent transparent
      ${({ theme }) => theme.globalConstants.color.borderGray};
  }

  &::after {
    margin-bottom: -1px;
    border-width: 0 6px 6px;
    border-color: transparent transparent
      ${({ theme }) => theme.globalConstants.color.white};
    transform: translateX(-1px);
  }
`;

const AutosuggestTooltip = styled(ControlTooltip)`
  color: #7569e9;
  button:hover + & {
    visibility: visible;
    opacity: 1;
  }

  @media (max-width: 600px) {
    position: static;
    display: flex;
    box-sizing: border-box;
    width: calc(200% + 8px);
    margin-left: calc(-100% - 8px);
    padding: 8px 0 0;
    align-items: flex-start;
    gap: 6px;
    visibility: visible;
    border: 0;
    background: transparent;
    box-shadow: none;
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
    opacity: 1;
    text-align: left;

    &::before,
    &::after {
      display: none;
    }
  }
`;

const FilterControl = styled.div`
  position: relative;

  &:hover > [role="tooltip"] {
    visibility: visible;
    opacity: 1;
  }
`;

const DirectoryControls: React.FC<{
  filters: StagedFilters;
  filtersOpen: boolean;
  activeFilterCount: number;
  autosuggestActive: boolean;
  disabled: boolean;
  onChange: (filters: StagedFilters) => void;
  onSearch: () => void;
  onClearSearch: () => void;
  onToggleAutosuggest: () => void;
  onToggleFilters: () => void;
}> = ({
  filters,
  filtersOpen,
  activeFilterCount,
  autosuggestActive,
  disabled,
  onChange,
  onClearSearch,
  onSearch,
  onToggleAutosuggest,
  onToggleFilters,
}) => (
  <Controls
    role="search"
    onSubmit={(event) => {
      event.preventDefault();
      onSearch();
    }}
  >
    <SearchField>
      <SearchInput
        aria-label={
          autosuggestActive
            ? "Search disabled while Auto-Suggest is on"
            : "Search profiles"
        }
        leadingIcon={<SearchIcon aria-hidden="true" />}
        size="md"
        placeholder={
          autosuggestActive
            ? "Search is disabled while Auto-Suggest is on."
            : "Search by name, interests, or teammates..."
        }
        value={autosuggestActive ? "" : filters.textQuery}
        disabled={autosuggestActive}
        onChange={(event) =>
          onChange({ ...filters, textQuery: event.target.value })
        }
      />
      {!autosuggestActive && filters.textQuery.length > 0 && (
        <ClearSearchButton
          type="button"
          aria-label="Clear search"
          onClick={(event) => {
            onClearSearch();
            event.currentTarget.parentElement
              ?.querySelector<HTMLInputElement>("input")
              ?.focus();
          }}
        >
          <XMarkIcon aria-hidden="true" />
        </ClearSearchButton>
      )}
    </SearchField>
    <Actions>
      <FilterControl>
        <FilterButton
          $active={filtersOpen}
          aria-controls="directory-filter-panel"
          aria-expanded={filtersOpen}
          aria-describedby={
            autosuggestActive ? "filters-disabled-description" : undefined
          }
          size="md"
          type="button"
          disabled={disabled || autosuggestActive}
          onClick={onToggleFilters}
        >
          Filters
          {!autosuggestActive && activeFilterCount > 0
            ? ` (${activeFilterCount})`
            : ""}
          <FunnelIcon aria-hidden="true" />
        </FilterButton>
        {autosuggestActive && (
          <ControlTooltip id="filters-disabled-description" role="tooltip">
            Filters are disabled while Auto-Suggest is on.
          </ControlTooltip>
        )}
      </FilterControl>
      <AutosuggestControl>
        <AutosuggestButton
          aria-checked={autosuggestActive}
          aria-describedby="autosuggest-description"
          aria-label="Auto-Suggest"
          role="switch"
          size="md"
          type="button"
          disabled={disabled && !autosuggestActive}
          onClick={onToggleAutosuggest}
        >
          <AutosuggestSparklesIcon aria-hidden="true" />
          <span>Auto-Suggest</span>
          <AutosuggestSwitch $active={autosuggestActive} aria-hidden="true" />
        </AutosuggestButton>
        <AutosuggestTooltip id="autosuggest-description" role="tooltip">
          <AutosuggestDescriptionIcon
            src={autosuggestSparklesIconUrl}
            alt=""
            aria-hidden="true"
          />
          <span>
            <b>Auto-Suggest:</b> Find hackers whose profiles are a strong match
            for yours.
          </span>
        </AutosuggestTooltip>
      </AutosuggestControl>
    </Actions>
  </Controls>
);

export default DirectoryControls;
